import { isAxiosError } from 'axios';

import { API_ERROR_CODES, type ApiErrorBody, type ApiErrorCode } from './types';

/**
 * A normalised API failure.
 *
 * Every rejection that leaves the HTTP layer is one of these, so no caller ever
 * has to ask "is this an AxiosError, a TypeError from the network, or a thrown
 * string?" before deciding what to render.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly fieldErrors: Readonly<Record<string, string[]>>;
  readonly context: Readonly<Record<string, unknown>>;

  constructor(init: {
    message: string;
    status: number;
    code: ApiErrorCode;
    fieldErrors?: Record<string, string[]>;
    context?: Record<string, unknown>;
    cause?: unknown;
  }) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.fieldErrors = init.fieldErrors ?? {};
    this.context = init.context ?? {};
  }

  get isValidation(): boolean {
    return this.status === 422;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /**
   * Whether retrying the identical request could plausibly succeed. Drives
   * TanStack Query's retry predicate: a 422 will fail identically forever, a 503
   * will not.
   */
  get isRetryable(): boolean {
    return this.status === 0 || this.status === 408 || this.status === 429 || this.status >= 500;
  }

  /** First message for a field, for inline form display. */
  fieldError(field: string): string | undefined {
    return this.fieldErrors[field]?.[0];
  }
}

function isErrorBody(value: unknown): value is ApiErrorBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    'message' in value &&
    typeof (value as { message: unknown }).message === 'string'
  );
}

/**
 * Turn anything thrown by the HTTP layer into an `ApiError`.
 *
 * The network branch matters more than it looks: `axios` reports a dropped
 * connection and an API 500 through the same rejection channel, and an admin
 * who is told "server error" when their wifi died will file the wrong bug.
 */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  if (isAxiosError(error)) {
    const response = error.response;

    if (!response) {
      const timedOut = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';

      return new ApiError({
        message: timedOut
          ? 'The request timed out. The API may be slow or unreachable.'
          : 'Could not reach the API. Check your connection and try again.',
        status: 0,
        code: API_ERROR_CODES.network,
        cause: error,
      });
    }

    const body: unknown = response.data;

    if (isErrorBody(body)) {
      return new ApiError({
        message: body.message,
        status: response.status,
        code: body.code ?? String(response.status),
        ...(body.errors ? { fieldErrors: body.errors } : {}),
        ...(body.context ? { context: body.context } : {}),
        cause: error,
      });
    }

    return new ApiError({
      message: `Unexpected response from the API (HTTP ${String(response.status)}).`,
      status: response.status,
      code: response.status >= 500 ? API_ERROR_CODES.serverError : String(response.status),
      cause: error,
    });
  }

  return new ApiError({
    message: error instanceof Error ? error.message : 'An unexpected error occurred.',
    status: 0,
    code: API_ERROR_CODES.serverError,
    cause: error,
  });
}
